import React, { useState } from 'react';
import { motion } from 'framer-motion';
// Header removed: branding moved into Sidebar
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import './App.css';

// Main App Component - Orchestrates the SSO Dashboard layout
// This component manages the overall structure and state for the Antasis Ecosystem SSO portal
// TypeScript: Ensures type-safe state management and component composition
// ARCHITECTURE: SSO handles authentication only - modules are separate applications

interface UserSession {
  email: string;
  role: string;
}

const App: React.FC = () => {
  const [user, setUser] = useState<UserSession | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);

  // Handle successful login
  const handleLoginSuccess = (email: string, role: string) => {
    setUser({ email, role });
    // Store user info for modules to access
    localStorage.setItem('userSession', JSON.stringify({ email, role }));
  };

  // Handle logout
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('userSession');
  };

  // Container animation variants for staggered child animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  // Show login page if user is not authenticated
  if (!user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Show dashboard if user is authenticated
  return (
    <motion.div
      className="app-wrapper"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="container">
        {/* Sidebar Navigation - Main application sections and settings */}
        <Sidebar 
          isOpen={isSidebarOpen} 
          onToggle={setIsSidebarOpen}
          userRole={user.role}
          onLogout={handleLogout}
        />

        {/* Main Content Area - Application grid and module links */}
        <MainContent 
          searchQuery={searchQuery} 
          onSearchChange={setSearchQuery}
        />
      </div>
    </motion.div>
  );
};

export default App;
